import {
	FontAwesome,
	Ionicons,
	MaterialCommunityIcons,
	MaterialIcons
} from "@expo/vector-icons";
import { createBottomTabNavigator } from "@react-navigation/bottom-tabs";
import { useSafeAreaInsets } from "react-native-safe-area-context";
import { useFocusEffect } from "@react-navigation/core";
import {
	createDrawerNavigator,
	DrawerContentScrollView,
	DrawerItem,
	DrawerItemList,
	useDrawerStatus
} from "@react-navigation/drawer";
import {
	DrawerActions,
	getFocusedRouteNameFromRoute
} from "@react-navigation/native";
import React from "react";
import { isMobile } from "react-device-detect";
import { LayoutAnimation, Text, TouchableOpacity, View } from "react-native";
import { Avatar } from "react-native-elements";
import { useDispatch, useSelector } from "react-redux";
import {
	DISPLAY_ALL,
	LOGOUT,
	SET_DISPLAY_FILTER_OPTION,
	SET_GOODDEAL_DISPLAY_FILTER_OPTION,
	SHOW_QR_CODE_SCANNER_MODAL
} from "../actions/types";
import { ROLE_GUEST } from "../config/constants";
import Ads from "../containers/Ads";
import AdsMap from "../containers/AdsMap";
import AppInfos from "../containers/AppInfos";
import ChatNeighbors from "../containers/ChatNeighbors";
import ChatEmployees from "../containers/ChatEmployees";
import ChatGroup from "../containers/ChatGroup";
import ChatUsers from "../containers/ChatUsers";
import CommunityDetails from "../containers/CommunityDetails";
import Live from "../containers/Live";
import Meteo from "../containers/Meteo";
import Settings from "../containers/Settings";
import { t } from "../services/i18n";
import commonStyles, {
	COLOR2,
	COLOR5,
	isiOS,
	isNative,
	WHITE_COLOR
} from "../styles/commonStyles";
import { DEFAULT_HEADER_STYLE, getCommunityUri } from "./CommonNavigation";
import { getAvatarSource } from "../utils/AvatarUtil";

const BottomTabs = createBottomTabNavigator();
const Drawer = createDrawerNavigator();

function CustomDrawerContent(props) {
	const dispatch = useDispatch();
	const { user } = useSelector(state => state.profileReducer);
	const isDrawerOpen = useDrawerStatus() === "open";

	const {
		mv20,
		mb20,
		mh20,
		fontBold,
		row,
		alignItemsCenter,
		justifyContentCenter,
		bgWhite,
		bgColor2,
		bgColor5
	} = commonStyles;
	const { navigation, displayFilter = false, displayDisconnect = true } = props;

	useFocusEffect(() => {
		navigation
			?.getParent()
			?.getParent()
			?.setOptions({
				// headerTitle: getHeaderTitle(route),
				headerLeft: () => (
					<TouchableOpacity
						style={[
							isDrawerOpen ? bgWhite : bgColor2,
							alignItemsCenter,
							justifyContentCenter,
							{
								// `@react-navigation/elements` v2 lays the header's left
								// slot out as a row sized by its content, so `flex: 1`
								// here would collapse the button to zero width and
								// `height: "100%"` to zero height. Stretch instead.
								alignSelf: "stretch",
								width: 56,
								paddingBottom: 3
							}
						]}
						onPress={() => navigation.dispatch(DrawerActions.toggleDrawer())}
					>
						<MaterialCommunityIcons
							name="menu"
							color={isDrawerOpen ? COLOR2 : WHITE_COLOR}
							size={28}
						/>
					</TouchableOpacity>
				),
				headerStyle:
					isDrawerOpen || displayFilter ? bgColor5 : DEFAULT_HEADER_STYLE
			});
	});

	return (
		<DrawerContentScrollView
			{...props}
			contentContainerStyle={[
				props.contentContainerStyle,
				// The drawer sits between the header and the tab bar, which already
				// consume the status-bar and home-indicator insets. v7 adds both to
				// the scroll content anyway (v6 added only the top one, and no bottom
				// padding at all), double-counting them here.
				{
					paddingTop: DRAWER_CONTENT_SPACING,
					paddingBottom: DRAWER_CONTENT_SPACING
				}
			]}
		>
			<TouchableOpacity
				onPress={() => {
					navigation.navigate("ProfileUpdateScreen");
					navigation.dispatch(DrawerActions.closeDrawer());
				}}
				disabled={!user || user?.role === ROLE_GUEST}
				style={[row, alignItemsCenter, isiOS ? mb20 : mv20]}
			>
				{user?.role === ROLE_GUEST ? (
					<Avatar
						rounded
						size={40}
						icon={{ name: "guest", type: "zocial" }}
						activeOpacity={0.7}
						containerStyle={[mh20, bgColor5]}
					/>
				) : (
					<Avatar
						rounded
						size={40}
						source={getAvatarSource(user?.photoUri, user?.gender)}
						title={user?.firstname?.toUpperCase().substr(0, 2)}
						activeOpacity={0.7}
						containerStyle={[mh20]}
					/>
				)}
				<Text style={[fontBold]}>
					{!user || user.role === ROLE_GUEST
						? t("profile:guest")
						: `${user?.firstname} ${user?.lastname}`}
				</Text>
			</TouchableOpacity>
			<DrawerItemList {...props} />
			{displayDisconnect && (
				<DrawerItem
					// `drawerItemStyle` only reaches the items rendered by
					// `DrawerItemList`; items declared here need it passed directly.
					style={DRAWER_ITEM_STYLE}
					label={t("button:disconnect")}
					icon={({ color, size }) => (
						<View
							style={[
								{ width: 30, height: 30 },
								alignItemsCenter,
								justifyContentCenter
							]}
						>
							<MaterialCommunityIcons name="logout" size={size} color={color} />
						</View>
					)}
					onPress={() => {
						dispatch({
							type: LOGOUT
						});
					}}
				/>
			)}
		</DrawerContentScrollView>
	);
}

function LiveDrawer(props) {
	const { navigation, route } = props;
	const { bgWhite, bgColor2, justifyContentCenter, alignItemsCenter } =
		commonStyles;

	const dispatch = useDispatch();
	const { filter, displayFilterOptions } = useSelector(
		state => state.liveReducer
	);
	const { currentCommunity } = useSelector(state => state.communityReducer);

	const isDefault = filter === DISPLAY_ALL && !displayFilterOptions;
	const routeName = getFocusedRouteNameFromRoute(route) ?? route.name;

	useFocusEffect(() => {
		navigation.getParent().setOptions({
			headerTitle: getHeaderTitle(route),
			headerRight: () =>
				routeName.startsWith("Live") && (
					<TouchableOpacity
						style={[
							isDefault ? bgColor2 : bgWhite,
							alignItemsCenter,
							justifyContentCenter,
							{
								// elements v2 always gives the header's right slot
								// `flexGrow: 1`, so `flex: 1` here would stretch the
								// button across the whole free width instead of the
								// intended 56pt square.
								alignSelf: "stretch",
								width: 56,
								paddingBottom: 3
							}
						]}
						onPress={() => {
							LayoutAnimation.easeInEaseOut();
							dispatch({
								type: SET_DISPLAY_FILTER_OPTION,
								payload: !displayFilterOptions
							});
						}}
					>
						<FontAwesome
							name="filter"
							color={isDefault ? WHITE_COLOR : COLOR2}
							size={isDefault ? 24 : 32}
							style={{
								backgroundColor: "transparent"
							}}
						/>
					</TouchableOpacity>
				)
		});
	});

	return (
		<Drawer.Navigator
			drawerContent={props => (
				<CustomDrawerContent
					{...props}
					displayFilter={displayFilterOptions}
					displayDisconnect={true}
				/>
			)}
			screenOptions={{
				headerShown: false,
				drawerActiveTintColor: WHITE_COLOR,
				drawerActiveBackgroundColor: COLOR2,
				drawerItemStyle: DRAWER_ITEM_STYLE,
				drawerType: "front",
				drawerContentContainerStyle: [bgWhite]
			}}
			edgeWidth={200}
		>
			<Drawer.Screen
				name="LiveScreen"
				component={Live}
				initialParams={{ communityUri: getCommunityUri(currentCommunity) }}
				options={{
					title: t("menu:live"),
					drawerIcon: ({ color, size }) => (
						<View
							style={[
								{ width: 30, height: 30 },
								alignItemsCenter,
								justifyContentCenter
							]}
						>
							<MaterialCommunityIcons
								name="animation-outline"
								size={size}
								color={color}
							/>
						</View>
					)
				}}
			/>
			<Drawer.Screen
				name="SettingsScreen"
				component={Settings}
				initialParams={{
					communityUri: getCommunityUri(currentCommunity)
				}}
				options={{
					title: t("menu:parameters"),
					drawerIcon: ({ color, size }) => (
						<View
							style={[
								{ width: 30, height: 30 },
								alignItemsCenter,
								justifyContentCenter
							]}
						>
							<Ionicons name="settings" size={size} color={color} />
						</View>
					),
					headerShown: false
				}}
			/>
		</Drawer.Navigator>
	);
}

function ChatDrawer(props) {
	const { navigation, route } = props;
	const { justifyContentCenter, alignItemsCenter } = commonStyles;
	const { currentCommunity } = useSelector(state => state.communityReducer);

	useFocusEffect(() => {
		navigation.getParent().setOptions({
			headerTitle: getHeaderTitle(route),
			headerRight: () => undefined
		});
	});
	return (
		<Drawer.Navigator
			drawerContent={props => <CustomDrawerContent {...props} />}
			screenOptions={{
				drawerActiveTintColor: WHITE_COLOR,
				drawerActiveBackgroundColor: COLOR2,
				drawerItemStyle: DRAWER_ITEM_STYLE,
				drawerType: "front",
				headerShown: false
			}}
			drawerStyle={{ width: 300 }}
			edgeWidth={200}
		>
			<Drawer.Screen
				name="ChatOwnScreen"
				component={ChatUsers}
				options={{
					title: t("chat:myconversations"),
					drawerIcon: ({ color, size }) => (
						<View
							style={[
								{ width: 30, height: 30 },
								alignItemsCenter,
								justifyContentCenter
							]}
						>
							<MaterialCommunityIcons name="chat" size={size} color={color} />
						</View>
					)
				}}
			/>
			<Drawer.Screen
				name="ChatGroupScreen"
				component={ChatGroup}
				initialParams={{ communityUri: getCommunityUri(currentCommunity) }}
				options={{
					title: t("chat:groupconversations"),
					drawerIcon: ({ color, size }) => (
						<View
							style={[
								{ width: 30, height: 30 },
								alignItemsCenter,
								justifyContentCenter
							]}
						>
							<Ionicons name="chatbubbles" size={size} color={color} />
						</View>
					)
				}}
			/>
			<Drawer.Screen
				name="ChatEmployeesScreen"
				component={ChatEmployees}
				initialParams={{ communityUri: getCommunityUri(currentCommunity) }}
				options={{
					title: t("chat:atmyservice"),
					drawerIcon: ({ color, size }) => (
						<View
							style={[
								{ width: 30, height: 30 },
								alignItemsCenter,
								justifyContentCenter
							]}
						>
							<FontAwesome name="handshake-o" size={size} color={color} />
						</View>
					)
				}}
			/>
			<Drawer.Screen
				name="ChatNeighborsScreen"
				component={ChatNeighbors}
				initialParams={{ communityUri: getCommunityUri(currentCommunity) }}
				options={{
					title: t("chat:coneighbors"),
					drawerIcon: ({ color, size }) => (
						<View
							style={[
								{ width: 30, height: 30 },
								alignItemsCenter,
								justifyContentCenter
							]}
						>
							<FontAwesome name="users" size={size} color={color} />
						</View>
					)
				}}
			/>
		</Drawer.Navigator>
	);
}

function AdsDrawer(props) {
	const dispatch = useDispatch();
	const { navigation, route } = props;
	const { bgWhite, bgColor2, justifyContentCenter, alignItemsCenter, row } =
		commonStyles;
	const { showQrCodeScannerModal } = useSelector(state => state.urlscanReducer);
	const { filter, displayFilterOptions } = useSelector(
		state => state.adReducer
	);
	const { currentCommunity } = useSelector(state => state.communityReducer);

	const isDefault = !filter.length && !displayFilterOptions;
	const routeName = getFocusedRouteNameFromRoute(route) ?? route.name;
	useFocusEffect(() => {
		navigation.getParent().setOptions({
			headerTitle: getHeaderTitle(route),
			headerRight: () =>
				(routeName === "AdsScreen" || routeName === "AdsTab") &&
				!showQrCodeScannerModal && (
					<View style={[row, { alignSelf: "stretch" }]}>
						{(isNative || isMobile) && (
							<TouchableOpacity
								style={[
									alignItemsCenter,
									justifyContentCenter,
									{
										alignSelf: "stretch",
										width: 56
									}
								]}
								onPress={() =>
									dispatch({
										type: SHOW_QR_CODE_SCANNER_MODAL
									})
								}
							>
								<MaterialCommunityIcons
									name="qrcode-scan"
									color={WHITE_COLOR}
									size={28}
								/>
							</TouchableOpacity>
						)}
						<TouchableOpacity
							style={[
								isDefault ? bgColor2 : bgWhite,
								alignItemsCenter,
								justifyContentCenter,
								{
									alignSelf: "stretch",
									width: 56,
									paddingBottom: 3
								}
							]}
							onPress={() => {
								LayoutAnimation.easeInEaseOut();
								dispatch({
									type: SET_GOODDEAL_DISPLAY_FILTER_OPTION,
									payload: !displayFilterOptions
								});
							}}
						>
							<FontAwesome
								name="filter"
								color={isDefault ? WHITE_COLOR : COLOR2}
								size={isDefault ? 24 : 32}
								style={{
									backgroundColor: "transparent"
								}}
							/>
						</TouchableOpacity>
					</View>
				)
		});
	});

	return (
		<Drawer.Navigator
			drawerContent={props => (
				<CustomDrawerContent {...props} displayFilter={displayFilterOptions} />
			)}
			screenOptions={{
				drawerActiveTintColor: WHITE_COLOR,
				drawerActiveBackgroundColor: COLOR2,
				drawerItemStyle: DRAWER_ITEM_STYLE,
				drawerType: "front",
				headerShown: false
			}}
			edgeWidth={200}
		>
			<Drawer.Screen
				name="AdsScreen"
				component={Ads}
				initialParams={{ communityUri: getCommunityUri(currentCommunity) }}
				options={{
					title: t("menu:localLife"),
					drawerIcon: ({ color, size }) => (
						<View
							style={[
								{ width: 30, height: 30 },
								alignItemsCenter,
								justifyContentCenter
							]}
						>
							<MaterialCommunityIcons
								name="map-marker-radius"
								size={size}
								color={color}
							/>
						</View>
					)
				}}
			/>
			<Drawer.Screen
				name="AdsMapScreen"
				component={AdsMap}
				initialParams={{ communityUri: getCommunityUri(currentCommunity) }}
				options={{
					title: t("menu:near"),
					drawerIcon: ({ color, size }) => (
						<View
							style={[
								{ width: 30, height: 30 },
								alignItemsCenter,
								justifyContentCenter
							]}
						>
							<MaterialCommunityIcons
								name="map-search-outline"
								size={size}
								color={color}
							/>
						</View>
					)
				}}
			/>
		</Drawer.Navigator>
	);
}

function MeteoDrawer(props) {
	const { navigation, route } = props;
	const { justifyContentCenter, alignItemsCenter } = commonStyles;
	const { currentCommunity } = useSelector(state => state.communityReducer);

	useFocusEffect(() => {
		navigation.getParent().setOptions({
			headerTitle: getHeaderTitle(route),
			headerRight: () => undefined
		});
	});

	return (
		<Drawer.Navigator
			drawerContent={props => <CustomDrawerContent {...props} />}
			screenOptions={{
				drawerActiveTintColor: WHITE_COLOR,
				drawerActiveBackgroundColor: COLOR2,
				drawerItemStyle: DRAWER_ITEM_STYLE,
				drawerType: "front",
				headerShown: false
			}}
			edgeWidth={200}
		>
			<Drawer.Screen
				name="MeteoScreen"
				component={Meteo}
				initialParams={{ communityUri: getCommunityUri(currentCommunity) }}
				options={{
					title: t("menu:weather"),
					drawerIcon: ({ color, size }) => (
						<View
							style={[
								{ width: 30, height: 30 },
								alignItemsCenter,
								justifyContentCenter
							]}
						>
							<MaterialCommunityIcons
								name="weather-partly-cloudy"
								size={size}
								color={color}
							/>
						</View>
					)
				}}
			/>
		</Drawer.Navigator>
	);
}

function CommunityDetailsDrawer(props) {
	const { navigation, route } = props;
	const { justifyContentCenter, alignItemsCenter } = commonStyles;
	const { currentCommunity } = useSelector(state => state.communityReducer);

	useFocusEffect(() => {
		navigation.getParent().setOptions({
			headerTitle: getHeaderTitle(route),
			headerRight: () => undefined
		});
	});

	return (
		<Drawer.Navigator
			drawerContent={props => <CustomDrawerContent {...props} />}
			screenOptions={{
				drawerActiveTintColor: WHITE_COLOR,
				drawerActiveBackgroundColor: COLOR2,
				drawerItemStyle: DRAWER_ITEM_STYLE,
				drawerType: "front",
				headerShown: false
			}}
			edgeWidth={200}
		>
			<Drawer.Screen
				name="CommunityDetailsScreen"
				component={CommunityDetails}
				initialParams={{ communityUri: getCommunityUri(currentCommunity) }}
				options={{
					title: t("menu:neighborhood"),
					drawerIcon: ({ color, size }) => (
						<View
							style={[
								{ width: 30, height: 30 },
								alignItemsCenter,
								justifyContentCenter
							]}
						>
							<MaterialIcons name="apartment" size={size} color={color} />
						</View>
					)
				}}
			/>
			<Drawer.Screen
				name="AppInfosScreen"
				component={AppInfos}
				options={{
					title: t("menu:about"),
					drawerIcon: ({ color, size }) => (
						<View
							style={[
								{ width: 30, height: 30 },
								alignItemsCenter,
								justifyContentCenter
							]}
						>
							<Ionicons name="information-circle" size={size} color={color} />
						</View>
					)
				}}
			/>
		</Drawer.Navigator>
	);
}

function getHeaderTitle(route) {
	const routeName = getFocusedRouteNameFromRoute(route) ?? route.name;
	switch (routeName) {
		case "TabScreen":
		case "LiveTab":
		case "LiveScreen":
			return t("menu:live");
		case "ChatTab":
			return t("menu:chat");
		case "AdsTab":
		case "AdsScreen":
			return t("menu:localLife");
		case "AdsMapScreen":
			return t("menu:near");
		case "MeteoTab":
		case "MeteoScreen":
			return t("menu:weather");
		case "CommunityDetailsTab":
		case "CommunityDetailsScreen":
			return t("menu:neighborhood");
		case "SettingsScreen":
			return t("menu:parameters");
		case "ChatOwnScreen":
			return t("chat:myconversations");
		case "ChatEmployeesScreen":
			return t("chat:atmyservice");
		case "ChatNeighborsScreen":
			return t("chat:coneighbors");
		case "ChatGroupScreen":
			return t("chat:groupconversations");
		case "AppInfosScreen":
			return t("menu:about");
		default:
			return routeName;
	}
}

// Matches SPACING in `@react-navigation/drawer`'s DrawerContentScrollView.
const DRAWER_CONTENT_SPACING = 12;

// `@react-navigation/drawer` v7 restyled DrawerItem: the default corner radius
// went from 4 to 56 (a pill) and the row grew from 47pt to 54pt (wrapper
// padding 8 -> 11 and an explicit 24pt label line-height). Neither the wrapper
// padding nor the label line-height is reachable through navigator options, so
// the row height is pinned back to the v6 value and the content is centred in
// it.
const DRAWER_ITEM_STYLE = {
	borderRadius: 4,
	height: 47,
	justifyContent: "center"
};

// Height of the tab bar content area, mirroring TABBAR_HEIGHT_UIKIT in
// `@react-navigation/bottom-tabs`.
const TAB_BAR_CONTENT_HEIGHT = 49;

export default function BottomTabsNavigator() {
	const insets = useSafeAreaInsets();
	// bottom-tabs v7 reserves the full bottom inset on the tab bar container
	// (v6 trimmed 4pt on iOS) and applies `tabBarItemStyle` to a wrapper around
	// the coloured button rather than to the button itself, so the 5pt bottom
	// padding no longer sits inside the tab colour. Together those shrank the
	// coloured band from 49pt to 44pt. Restore the v6 geometry explicitly.
	const tabBarBottomInset = Math.max(insets.bottom - 4, 0);
	const { cptNewAds } = useSelector(state => state.adReducer);
	const { totalNotRead } = useSelector(state => state.chatReducer);
	const { cptNewEvents } = useSelector(state => state.liveReducer);
	const { currentCommunity } = useSelector(state => state.communityReducer);

	return (
		<BottomTabs.Navigator
			screenOptions={{
				headerShown: false,
				tabBarActiveTintColor: "white",
				tabBarInactiveTintColor: "white",
				tabBarActiveBackgroundColor: COLOR2,
				tabBarInactiveBackgroundColor: COLOR5,
				tabBarStyle: {
					height: TAB_BAR_CONTENT_HEIGHT + tabBarBottomInset,
					paddingBottom: tabBarBottomInset
				},
				tabBarLabelPosition: "below-icon"
			}}
		>
			<BottomTabs.Screen
				name="LiveTab"
				component={LiveDrawer}
				options={{
					tabBarLabel: t("menu:live"),
					tabBarIcon: ({ color, size }) => (
						<MaterialCommunityIcons
							name="animation-outline"
							size={size}
							color={color}
						/>
					),
					tabBarBadge: cptNewEvents ? cptNewEvents : undefined
				}}
			/>
			<BottomTabs.Screen
				name="ChatTab"
				component={ChatDrawer}
				options={{
					tabBarLabel: t("menu:chat"),
					tabBarIcon: ({ color, size }) => (
						<Ionicons name="chatbubbles" size={size} color={color} />
					),
					tabBarBadge: totalNotRead ? totalNotRead : undefined
				}}
			/>
			{currentCommunity?.gooddeal && (
				<BottomTabs.Screen
					name="AdsTab"
					component={AdsDrawer}
					options={{
						tabBarLabel: t("menu:localLife"),
						tabBarIcon: ({ color, size }) => (
							<MaterialCommunityIcons
								name="map-marker-radius"
								size={size}
								color={color}
							/>
						),
						tabBarBadge: cptNewAds ? cptNewAds : undefined
					}}
				/>
			)}
			{currentCommunity?.weather && (
				<BottomTabs.Screen
					name="MeteoTab"
					component={MeteoDrawer}
					options={{
						tabBarLabel: t("menu:weather"),
						tabBarIcon: ({ color, size }) => (
							<MaterialCommunityIcons
								name="weather-partly-cloudy"
								size={size}
								color={color}
							/>
						)
					}}
				/>
			)}
			<BottomTabs.Screen
				name="CommunityDetailsTab"
				component={CommunityDetailsDrawer}
				options={{
					tabBarLabel: t("menu:infos"),
					tabBarIcon: ({ color, size }) => (
						<Ionicons name="information-circle" size={size} color={color} />
					)
				}}
			/>
		</BottomTabs.Navigator>
	);
}
